import { YES_NO, applicantSection, otherAttachments, replySection } from "modules/ebiForms/common";
import { entitySection } from "modules/ebiForms/definitions/sharedPlanningTaxes";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiField, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * A6. Informacje i deklaracje na podatek od nieruchomości, rolny i leśny — art. 6 ust. 6 i 9 upol, art. 6a ust. 5 i 8
 * ustawy o podatku rolnym, art. 6 ust. 2 i 5 ustawy o podatku leśnym. Urzędowe wzory z rozporządzeń MF: Dz.U. 2019
 * poz. 1104 (IN-1, ZIN-1–3, DN-1, ZDN-1–2), poz. 1105 (IR-1, DR-1 …), poz. 1126 (IL-1, DL-1 …) —
 * https://dziennikustaw.gov.pl/D2019000110401.pdf, https://www.dziennikustaw.gov.pl/DU/2019/1105/D2019000110501.pdf,
 * https://dziennikustaw.gov.pl/DU/2019/1126/D2019000112601.pdf, pobrane i odczytane 22.10.2026 (PDF-y z podatki.gov.pl
 * to formularze XFA bez czytelnej treści). Formularz jest wariantem w BE (6 wzorów). Odwzorowano części A–F wzorów:
 * przedmioty opodatkowania jako pola liczbowe (m² i zł — 2 miejsca, ha — 4 miejsca jak w objaśnieniach), w deklaracjach
 * stawka i kwota przy każdej pozycji, zobowiązanie i raty; załączniki Z*-1, Z*-2 i Z*-3 jako listy w formularzu.
 * Uproszczenia: w DR-1 przeliczniki na ha przeliczeniowe podaje się łącznie (E.6), nie przy każdej klasie; dane podatnika
 * bez PESEL (data urodzenia, imiona rodziców) pominięte — zalogowany ma PESEL z mObywatela. Pełnomocnictwo — UPL-1 /
 * UPL-1P (art. 80a OP) bez opłaty 17 zł według katalogu, dlatego bez bloku wspólnego pełnomocnictwa. Sum EBI nie liczy.
 */
const CHOSEN: EbiCondition = { field: "formularz", filled: true };
const INFO: EbiCondition = { field: "formularz", in: ["in1", "ir1", "il1"] };
const DECL: EbiCondition = { field: "formularz", in: ["dn1", "dr1", "dl1"] };
const PROPERTY: EbiCondition = { field: "formularz", in: ["in1", "dn1"] };
const AGRI: EbiCondition = { field: "formularz", in: ["ir1", "dr1"] };
const FOREST: EbiCondition = { field: "formularz", in: ["il1", "dl1"] };
const AGRI_DECL: EbiCondition = { field: "formularz", equals: "dr1" };

const OTHER_PERSON: EbiCondition = {
  any: [
    { all: [INFO, { field: "podatnikInformacja", equals: "innaOsoba" }] },
    { all: [DECL, { field: "podatnikDeklaracja", equals: "innaOsoba" }] },
  ],
};
const SELF: EbiCondition = {
  any: [
    { all: [INFO, { field: "podatnikInformacja", equals: "skladajacy" }] },
    { all: [DECL, { field: "podatnikDeklaracja", equals: "skladajacy" }] },
  ],
};
const ENTITY: EbiCondition = { all: [DECL, { field: "podatnikDeklaracja", in: ["osobaPrawna", "jednostka"] }] };
const REPRESENTING: EbiCondition = { any: [OTHER_PERSON, ENTITY] };
const JOINT: EbiCondition = {
  all: [INFO, { field: "rodzajPodmiotu", equals: "wspolwlasciciel" }, { field: "wspolnieZPozostalymi", equals: "tak" }],
};
const FARM: EbiCondition = { all: [AGRI, { field: "gospodarstwo", equals: "posiadam" }] };

const HA = 4;
const M2 = 2;

/** Pozycja części D: podstawa opodatkowania, a w deklaracji także stawka z uchwały i kwota podatku za okres deklaracji. */
const taxRow = (code: string, label: string, short: string, unit: string, decimals?: number, rateUnit = "zł"): IEbiField[] => [
  { code, type: "number", label: `${label} [${unit}]`, decimals, min: 0, width: "third" },
  { code: `${code}Stawka`, type: "number", label: `Stawka podatku [${rateUnit}] — ${short}`, decimals: 2, min: 0, width: "third", visibleWhen: DECL },
  { code: `${code}Kwota`, type: "number", label: `Kwota podatku [zł] — ${short}`, decimals: 2, min: 0, width: "third", visibleWhen: DECL },
];

/** Budynki: powierzchnia użytkowa ogółem + w tym kondygnacje 1,40–2,20 m (liczone w 50%) i powyżej 2,20 m. */
const buildingRow = (code: string, label: string, short: string): IEbiField[] => [
  ...taxRow(code, `${label} — ogółem`, short, "m²", M2),
  { code: `${code}Niskie`, type: "number", label: `${short} — w tym kondygnacje o wysokości od 1,40 do 2,20 m [m²]`, decimals: M2, min: 0, width: "half" },
  { code: `${code}Wysokie`, type: "number", label: `${short} — w tym kondygnacje o wysokości powyżej 2,20 m [m²]`, decimals: M2, min: 0, width: "half" },
];

/** „Inne” przedmioty wg uchwały rady gminy (D.1.1, D.2.1, D.3.1): lista do informacji i lista ze stawką do deklaracji. */
const otherRows = (code: string, label: string, unit: string, decimals?: number, rateUnit = "zł"): IEbiField[] => {
  const base: IEbiField[] = [
    {
      code: "rodzaj",
      type: "text",
      label: "Rodzaj przedmiotu opodatkowania (nazwa albo jednostka redakcyjna uchwały rady gminy)",
      maxLength: 300,
    },
    { code: "podstawa", type: "number", label: `Podstawa opodatkowania [${unit}]`, decimals, min: 0, width: "third" },
  ];

  return [
    { code, type: "repeat", label: `${label} (nieobowiązkowo)`, itemLabel: "Pozycja", max: 10, visibleWhen: INFO, fields: base },
    {
      code: `${code}Deklaracja`,
      type: "repeat",
      label: `${label} (nieobowiązkowo)`,
      itemLabel: "Pozycja",
      max: 10,
      visibleWhen: DECL,
      fields: [
        ...base,
        { code: "stawka", type: "number", label: `Stawka podatku [${rateUnit}]`, decimals: 2, min: 0, width: "third" },
        { code: "kwota", type: "number", label: "Kwota podatku [zł]", decimals: 2, min: 0, width: "third" },
      ],
    },
  ];
};

const SUBJECT_KINDS = [
  { value: "grunt", label: "grunt" },
  { value: "budynek", label: "budynek lub jego część" },
  { value: "budowla", label: "budowla lub jej część związana z działalnością gospodarczą" },
  { value: "uzytekRolny", label: "użytek rolny" },
  { value: "las", label: "las" },
];

/** Kolumny załączników Z*-1 i Z*-2: położenie i oznaczenie przedmiotu opodatkowania. */
const subjectFields: IEbiField[] = [
  { code: "rodzaj", type: "select", label: "Przedmiot opodatkowania", required: true, width: "half", options: SUBJECT_KINDS },
  { code: "polozenie", type: "text", label: "Położenie (adres, w tym dzielnica)", required: true, width: "half", maxLength: 300 },
  { code: "kw", type: "text", label: "Nr księgi wieczystej (zbioru dokumentów)", width: "third", maxLength: 30 },
  { code: "obreb", type: "text", label: "Nr obrębu (arkusza mapy)", required: true, width: "third", maxLength: 50 },
  { code: "dzialka", type: "text", label: "Nr działki", required: true, width: "third", maxLength: 50 },
  {
    code: "wielkosc",
    type: "number",
    label: "Powierzchnia [m² albo ha], powierzchnia użytkowa budynku [m²] albo wartość budowli [zł]",
    required: true,
    decimals: HA,
    min: 0,
    width: "half",
  },
];

const A6: IEbiFormDefinition = {
  code: "A6",
  version: 1,
  title: "Informacja albo deklaracja na podatek od nieruchomości, rolny lub leśny",
  category: EEbiFormCategory.Taxes,
  office: EEbiFormOffice.Municipality,
  documentType: "Deklaracja",
  description:
    "Informacje IN-1, IR-1, IL-1 (osoby fizyczne) i deklaracje DN-1, DR-1, DL-1 (osoby prawne i jednostki organizacyjne) — urzędowe wzory Ministra Finansów.",
  keywords: ["podatek od nieruchomości", "podatek rolny", "podatek leśny", "informacja", "deklaracja", "IN-1", "DN-1", "IR-1", "DR-1", "IL-1", "DL-1"],
  organ: "Wójt / Burmistrz / Prezydent Miasta jako organ podatkowy",
  legalBasis:
    "art. 6 ust. 6 i 9 ustawy z dnia 12 stycznia 1991 r. o podatkach i opłatach lokalnych; art. 6a ust. 5 i 8 ustawy z dnia 15 listopada 1984 r. o podatku rolnym; art. 6 ust. 2 i 5 ustawy z dnia 30 października 2002 r. o podatku leśnym; rozporządzenia Ministra Finansów z dnia 30 maja 2019 r. (Dz.U. poz. 1104 i 1105) i z dnia 3 czerwca 2019 r. (Dz.U. poz. 1126)",
  request: "Składam informację albo deklarację podatkową wskazaną poniżej wraz z danymi o przedmiotach opodatkowania.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty.",
  deadlineLabel: "14 dni od zmiany",
  deadline:
    "Informacja — 14 dni od zdarzenia (podatek ustala decyzja); deklaracja DN-1 — do 31 stycznia, DR-1 i DL-1 — do 15 stycznia roku podatkowego albo 14 dni od zdarzenia.",
  variantField: "formularz",
  variants: {
    in1: { title: "Informacja o nieruchomościach i obiektach budowlanych (IN-1)" },
    ir1: { title: "Informacja o gruntach — podatek rolny (IR-1)" },
    il1: { title: "Informacja o lasach (IL-1)" },
    dn1: { title: "Deklaracja na podatek od nieruchomości (DN-1)" },
    dr1: { title: "Deklaracja na podatek rolny (DR-1)" },
    dl1: { title: "Deklaracja na podatek leśny (DL-1)" },
  },
  sections: [
    {
      code: "formularz",
      title: "Formularz",
      description:
        "Informację składają osoby fizyczne (podatek ustala decyzja). Deklarację z wyliczeniem podatku składają osoby prawne, jednostki organizacyjne, w tym spółki bez osobowości prawnej, oraz osoby fizyczne będące ich współwłaścicielami lub współposiadaczami.",
      fields: [
        {
          code: "formularz",
          type: "radio",
          label: "Składam",
          required: true,
          options: [
            { value: "in1", label: "IN-1 — informację o nieruchomościach i obiektach budowlanych" },
            { value: "ir1", label: "IR-1 — informację o gruntach (podatek rolny)" },
            { value: "il1", label: "IL-1 — informację o lasach" },
            { value: "dn1", label: "DN-1 — deklarację na podatek od nieruchomości" },
            { value: "dr1", label: "DR-1 — deklarację na podatek rolny" },
            { value: "dl1", label: "DL-1 — deklarację na podatek leśny" },
          ],
        },
      ],
    },
    applicantSection("Składający"),
    {
      code: "cel",
      title: "A. Cel złożenia i B. podmiot zobowiązany",
      visibleWhen: CHOSEN,
      fields: [
        {
          code: "cel",
          type: "radio",
          label: "Cel złożenia formularza",
          required: true,
          defaultValue: "zlozenie",
          options: [
            { value: "zlozenie", label: "złożenie informacji albo deklaracji" },
            { value: "korekta", label: "korekta informacji albo deklaracji (zgodnie z przepisami Ordynacji podatkowej)" },
          ],
        },
        {
          code: "okresOd",
          type: "text",
          label: "Okres, od którego informacja obowiązuje (miesiąc i rok)",
          required: true,
          visibleWhen: INFO,
          width: "third",
          maxLength: 7,
          placeholder: "MM-RRRR",
        },
        { code: "rok", type: "number", label: "Rok, na który składana jest deklaracja", required: true, visibleWhen: DECL, min: 2000, max: 2100, width: "third" },
        {
          code: "miesiacOd",
          type: "number",
          label: "Okres, od którego deklaracja obowiązuje — miesiąc (1–12)",
          required: true,
          visibleWhen: DECL,
          min: 1,
          max: 12,
          width: "third",
        },
        {
          code: "rodzajPodmiotu",
          type: "radio",
          label: "Rodzaj podmiotu",
          required: true,
          options: [
            { value: "wlasciciel", label: "właściciel, użytkownik wieczysty lub posiadacz" },
            { value: "wspolwlasciciel", label: "współwłaściciel, współużytkownik wieczysty lub współposiadacz" },
          ],
        },
      ],
    },
    {
      code: "podatnik",
      title: "C. Dane podatnika",
      visibleWhen: CHOSEN,
      fields: [
        {
          code: "podatnikInformacja",
          type: "radio",
          label: "Podatnik",
          required: true,
          visibleWhen: INFO,
          defaultValue: "skladajacy",
          options: [
            { value: "skladajacy", label: "jestem podatnikiem — dane jak w części „Składający”" },
            { value: "innaOsoba", label: "podatnikiem jest inna osoba fizyczna, w której imieniu działam" },
          ],
        },
        {
          code: "podatnikDeklaracja",
          type: "radio",
          label: "Rodzaj podatnika",
          required: true,
          visibleWhen: DECL,
          options: [
            { value: "osobaPrawna", label: "osoba prawna" },
            { value: "jednostka", label: "jednostka organizacyjna, w tym spółka nieposiadająca osobowości prawnej" },
            { value: "skladajacy", label: "osoba fizyczna — to ja (współwłaściciel lub współposiadacz z osobą prawną albo jednostką organizacyjną)" },
            { value: "innaOsoba", label: "osoba fizyczna, w której imieniu działam" },
          ],
        },
        { code: "regon", type: "text", label: "Identyfikator REGON (o ile został nadany)", visibleWhen: SELF, width: "half", maxLength: 14 },
        { code: "innaNazwisko", type: "text", label: "Nazwisko podatnika", required: true, visibleWhen: OTHER_PERSON, width: "half", maxLength: 100 },
        { code: "innaImie", type: "text", label: "Pierwsze imię podatnika", required: true, visibleWhen: OTHER_PERSON, width: "half", maxLength: 100 },
        {
          code: "innaIdentyfikator",
          type: "text",
          label: "Identyfikator podatkowy — PESEL albo NIP",
          required: true,
          visibleWhen: OTHER_PERSON,
          width: "half",
          maxLength: 20,
          hint: "NIP podają osoby prowadzące działalność gospodarczą lub zarejestrowane jako podatnicy VAT, pozostali — PESEL.",
        },
        { code: "innaRegon", type: "text", label: "Identyfikator REGON (o ile został nadany)", visibleWhen: OTHER_PERSON, width: "half", maxLength: 14 },
        { code: "innaAdres", type: "text", label: "Adres zamieszkania — ulica, numer domu i lokalu", required: true, visibleWhen: OTHER_PERSON, maxLength: 200 },
        { code: "innaKodPocztowy", type: "postalCode", label: "Kod pocztowy", required: true, visibleWhen: OTHER_PERSON, width: "third", placeholder: "00-000" },
        { code: "innaMiejscowosc", type: "text", label: "Miejscowość", required: true, visibleWhen: OTHER_PERSON, width: "twoThirds", maxLength: 100 },
        {
          code: "umocowanie",
          type: "radio",
          label: "W imieniu podatnika działam",
          required: true,
          visibleWhen: REPRESENTING,
          options: [
            { value: "pelnomocnictwo", label: "na podstawie pełnomocnictwa do podpisywania informacji lub deklaracji (UPL-1P albo UPL-1)" },
            { value: "ustawowe", label: "jako przedstawiciel ustawowy, członek organu albo wspólnik uprawniony do reprezentacji" },
          ],
        },
      ],
    },
    entitySection("C. Dane podatnika — osoba prawna albo jednostka organizacyjna", ENTITY),
    {
      code: "nieruchomosci",
      title: "D. Dane o przedmiotach opodatkowania podlegających opodatkowaniu — podatek od nieruchomości",
      visibleWhen: PROPERTY,
      description:
        "Zostaw puste pozycje, które Cię nie dotyczą. Do powierzchni użytkowej budynku zalicza się powierzchnię mierzoną po wewnętrznej długości ścian na wszystkich kondygnacjach (bez klatek schodowych i szybów dźwigowych); kondygnacje o wysokości od 1,40 do 2,20 m liczy się w 50%, niższe pomija się. W deklaracji podaj przy każdej pozycji stawkę z uchwały rady gminy i kwotę podatku za okres deklaracji.",
      fields: [
        ...taxRow(
          "gruntyDzialalnosc",
          "D.1. Grunty związane z prowadzeniem działalności gospodarczej, bez względu na sposób zakwalifikowania w ewidencji gruntów i budynków",
          "grunty związane z działalnością gospodarczą",
          "m²",
          M2,
        ),
        ...taxRow(
          "gruntyWody",
          "D.1. Grunty pod wodami powierzchniowymi stojącymi lub wodami powierzchniowymi płynącymi jezior i zbiorników sztucznych",
          "grunty pod wodami",
          "ha",
          HA,
        ),
        ...taxRow(
          "gruntyPozostale",
          "D.1. Grunty pozostałe, w tym zajęte na prowadzenie odpłatnej statutowej działalności pożytku publicznego przez organizacje pożytku publicznego",
          "grunty pozostałe",
          "m²",
          M2,
        ),
        ...taxRow(
          "gruntyRewitalizacja",
          "D.1. Grunty niezabudowane objęte obszarem rewitalizacji, o którym mowa w ustawie z dnia 9 października 2015 r. o rewitalizacji, i położone na terenach, dla których miejscowy plan zagospodarowania przestrzennego przewiduje przeznaczenie pod zabudowę mieszkaniową, usługową albo zabudowę o przeznaczeniu mieszanym obejmującym wyłącznie te rodzaje zabudowy, jeżeli od dnia wejścia w życie tego planu w odniesieniu do tych gruntów upłynął okres 4 lat, a w tym czasie nie zakończono budowy zgodnie z przepisami prawa budowlanego",
          "grunty niezabudowane na obszarze rewitalizacji",
          "m²",
          M2,
        ),
        ...otherRows("inneGrunty", "D.1.1. Inne grunty — według uchwały rady gminy", "m² albo ha", HA),
        ...buildingRow("budMieszkalne", "D.2. Budynki mieszkalne", "budynki mieszkalne"),
        ...buildingRow(
          "budDzialalnosc",
          "D.2. Budynki związane z prowadzeniem działalności gospodarczej oraz budynki mieszkalne lub ich części zajęte na prowadzenie działalności gospodarczej",
          "budynki związane z działalnością gospodarczą",
        ),
        ...buildingRow(
          "budMaterialSiewny",
          "D.2. Budynki zajęte na prowadzenie działalności gospodarczej w zakresie obrotu kwalifikowanym materiałem siewnym",
          "budynki — obrót kwalifikowanym materiałem siewnym",
        ),
        ...buildingRow(
          "budZdrowie",
          "D.2. Budynki związane z udzielaniem świadczeń zdrowotnych w rozumieniu przepisów o działalności leczniczej, zajęte przez podmioty udzielające tych świadczeń",
          "budynki — świadczenia zdrowotne",
        ),
        ...buildingRow(
          "budPozostale",
          "D.2. Budynki pozostałe, w tym zajęte na prowadzenie odpłatnej statutowej działalności pożytku publicznego przez organizacje pożytku publicznego",
          "budynki pozostałe",
        ),
        ...otherRows("inneBudynki", "D.2.1. Inne budynki lub ich części — według uchwały rady gminy (powierzchnia użytkowa)", "m²", M2),
        ...taxRow(
          "budowle",
          "D.3. Budowle lub ich części związane z prowadzeniem działalności gospodarczej — wartość (po zaokrągleniu do pełnych złotych)",
          "budowle",
          "zł",
          undefined,
          "%",
        ),
        ...otherRows("inneBudowle", "D.3.1. Inne budowle lub ich części — według uchwały rady gminy (wartość)", "zł", undefined, "%"),
      ],
    },
    {
      code: "gospodarstwo",
      title: "D. Oświadczenie o posiadaniu gospodarstwa rolnego",
      visibleWhen: AGRI,
      fields: [
        {
          code: "gospodarstwo",
          type: "radio",
          label: "Oświadczenie",
          required: true,
          hint: "Gospodarstwo rolne to obszar gruntów sklasyfikowanych w ewidencji gruntów i budynków jako użytki rolne (z wyjątkiem zajętych na działalność gospodarczą inną niż rolnicza) o łącznej powierzchni przekraczającej 1 ha lub 1 ha przeliczeniowy.",
          options: [
            { value: "posiadam", label: "posiadam gospodarstwo rolne" },
            { value: "nieposiadam", label: "nie posiadam gospodarstwa rolnego" },
          ],
        },
      ],
    },
    {
      code: "uzytkiGospodarstwa",
      title: "E. Dane o użytkach rolnych stanowiących gospodarstwo rolne — podlegających opodatkowaniu",
      visibleWhen: FARM,
      description: "Powierzchnia w hektarach fizycznych, z dokładnością do czterech miejsc po przecinku. Zostaw puste klasy, których nie posiadasz.",
      fields: [
        ...["I", "II", "IIIa", "IIIb", "IVa", "IVb"].map(
          (cls): IEbiField => ({ code: `orne${cls}`, type: "number", label: `E.1. Grunty orne — klasa ${cls} [ha]`, decimals: HA, min: 0, width: "third" }),
        ),
        ...["I", "II", "III", "IV"].map(
          (cls): IEbiField => ({ code: `laki${cls}`, type: "number", label: `E.2. Łąki i pastwiska — klasa ${cls} [ha]`, decimals: HA, min: 0, width: "third" }),
        ),
        ...(
          [
            ["I", "I"],
            ["II", "II"],
            ["III", "III, IIIa"],
            ["IIIb", "IIIb"],
            ["IV", "IV, IVa"],
            ["IVb", "IVb"],
          ] as const
        ).map(([code, cls]): IEbiField => ({ code: `sady${code}`, type: "number", label: `E.3. Sady — klasa ${cls} [ha]`, decimals: HA, min: 0, width: "third" })),
        {
          code: "stawyLososiowe",
          type: "number",
          label:
            "E.4. Grunty pod stawami zarybionymi łososiem, trocią, głowacicą, palią i pstrągiem, grunty rolne zabudowane oraz użytki rolne o nieustalonym przeliczniku powierzchni [ha]",
          decimals: HA,
          min: 0,
          width: "half",
        },
        {
          code: "stawyInne",
          type: "number",
          label: "E.5. Grunty pod stawami zarybionymi innymi gatunkami ryb, grunty pod stawami niezarybionymi, grunty pod rowami [ha]",
          decimals: HA,
          min: 0,
          width: "half",
        },
        {
          code: "gospodarstwoHa",
          type: "number",
          label: "E.6. Łączna powierzchnia użytków rolnych stanowiących gospodarstwo rolne [ha]",
          required: true,
          visibleWhen: AGRI_DECL,
          decimals: HA,
          min: 0,
          width: "half",
        },
        {
          code: "gospodarstwoHaPrzeliczeniowe",
          type: "number",
          label: "E.6. Łączna powierzchnia użytków rolnych stanowiących gospodarstwo rolne [ha przeliczeniowe]",
          required: true,
          visibleWhen: AGRI_DECL,
          decimals: HA,
          min: 0,
          width: "half",
          hint: "Suma części E.1–E.5 po przeliczeniu przelicznikami właściwymi dla okręgu podatkowego.",
        },
      ],
    },
    {
      code: "uzytkiPozaGospodarstwem",
      title: "F. Dane o użytkach rolnych niestanowiących gospodarstwa rolnego — podlegających opodatkowaniu",
      visibleWhen: AGRI,
      fields: [{ code: "uzytkiPozaGospodarstwem", type: "number", label: "Powierzchnia [ha]", decimals: HA, min: 0, width: "third" }],
    },
    {
      code: "lasy",
      title: "D. Dane o lasach podlegających opodatkowaniu",
      visibleWhen: FOREST,
      description: "Powierzchnia w hektarach, z dokładnością do czterech miejsc po przecinku.",
      fields: [
        ...taxRow("lasy", "Lasy (z wyjątkiem lasów wchodzących w skład rezerwatów przyrody i parków narodowych)", "lasy", "ha", HA),
        ...taxRow("lasyRezerwaty", "Lasy wchodzące w skład rezerwatów przyrody i parków narodowych", "lasy w rezerwatach i parkach narodowych", "ha", HA),
      ],
    },
    {
      code: "zobowiazanie",
      title: "Wysokość zobowiązania podatkowego i rat podatku",
      visibleWhen: DECL,
      fields: [
        {
          code: "rolnyPodstawa",
          type: "number",
          label: "Podstawa opodatkowania [ha przeliczeniowe albo ha]",
          required: true,
          visibleWhen: AGRI_DECL,
          decimals: HA,
          min: 0,
          width: "third",
          hint: "Gospodarstwo rolne — powierzchnia w ha przeliczeniowych (E.6); użytki niestanowiące gospodarstwa — powierzchnia z części F.",
        },
        { code: "rolnyStawka", type: "number", label: "Stawka podatku rolnego [zł]", required: true, visibleWhen: AGRI_DECL, decimals: 2, min: 0, width: "third" },
        {
          code: "rolnyKwota",
          type: "number",
          label: "Kwota podatku za okres, którego dotyczy deklaracja [zł]",
          required: true,
          visibleWhen: AGRI_DECL,
          decimals: 2,
          min: 0,
          width: "third",
        },
        {
          code: "rolnyUlga",
          type: "number",
          label: "Kwota przysługującej ulgi w podatku rolnym za okres, którego dotyczy deklaracja [zł]",
          visibleWhen: AGRI_DECL,
          decimals: 2,
          min: 0,
          width: "half",
        },
        {
          code: "lacznaKwota",
          type: "number",
          label: "Łączna kwota podatku za okres, którego dotyczy deklaracja [zł]",
          required: true,
          visibleWhen: { not: AGRI_DECL },
          decimals: 2,
          min: 0,
          width: "half",
          hint: "Suma kwot podatku z części D.",
        },
        {
          code: "kwotaNieobjete",
          type: "number",
          label: "Kwota podatku za miesiące nieobjęte deklaracją [zł]",
          required: true,
          defaultValue: "0",
          decimals: 2,
          min: 0,
          width: "half",
          hint: "Przy złożeniu deklaracji wpisz 0; przy korekcie — sumę miesięcznych kwot podatku za miesiące nieobjęte korektą.",
        },
        {
          code: "rolnyUlgaNieobjete",
          type: "number",
          label: "Kwota przysługującej ulgi w podatku rolnym za miesiące nieobjęte deklaracją [zł]",
          visibleWhen: AGRI_DECL,
          decimals: 2,
          min: 0,
          width: "half",
        },
        {
          code: "zobowiazanie",
          type: "number",
          label: "Wysokość zobowiązania podatkowego [zł]",
          required: true,
          min: 0,
          width: "half",
          hint: "Suma kwot podatku za okres deklaracji i za miesiące nieobjęte deklaracją (w podatku rolnym pomniejszona o ulgi), zaokrąglona do pełnych złotych.",
        },
        {
          code: "raty",
          type: "repeat",
          label: "Kwoty rat podatku",
          itemLabel: "Rata",
          required: true,
          min: 1,
          max: 12,
          hint: "Suma rat musi być równa wysokości zobowiązania; w ostatniej racie następuje wyrównanie. Terminy: DN-1 — I rata do 31 stycznia, kolejne do 15. dnia każdego miesiąca; DR-1 — do 15 marca, 15 maja, 15 września i 15 listopada; DL-1 — do 15. dnia każdego miesiąca. Podatek do 100 zł płaci się jednorazowo w terminie pierwszej raty.",
          fields: [{ code: "kwota", type: "number", label: "Kwota raty [zł]", required: true, min: 0, width: "half" }],
        },
      ],
    },
    {
      code: "przedmioty",
      title: "Dane o poszczególnych przedmiotach opodatkowania podlegających opodatkowaniu (załącznik ZIN-1, ZDN-1, ZIR-1, ZDR-1, ZIL-1 albo ZDL-1)",
      visibleWhen: CHOSEN,
      fields: [
        {
          code: "przedmioty",
          type: "repeat",
          label: "Przedmioty opodatkowania",
          itemLabel: "Przedmiot opodatkowania",
          required: true,
          min: 1,
          max: 100,
          fields: [
            ...subjectFields,
            {
              code: "formaWladania",
              type: "select",
              label: "Forma władania",
              required: true,
              width: "half",
              options: [
                { value: "wlasnosc", label: "własność" },
                { value: "uzytkowanieWieczyste", label: "użytkowanie wieczyste" },
                { value: "posiadanieSamoistne", label: "posiadanie samoistne" },
                { value: "posiadanieZalezne", label: "posiadanie zależne" },
                { value: "bezTytulu", label: "posiadanie bez tytułu prawnego" },
              ],
            },
            { code: "nazwaBudowli", type: "text", label: "Nazwa budowli i nr inwentarzowy (tylko budowle)", width: "half", maxLength: 200 },
            { code: "uzytek", type: "text", label: "Rodzaj i klasa użytku (podatek rolny)", width: "half", maxLength: 100 },
            { code: "ulga", type: "text", label: "Podstawa prawna ulgi (podatek rolny)", maxLength: 300 },
          ],
        },
      ],
    },
    {
      code: "zwolnione",
      title: "Dane o przedmiotach opodatkowania zwolnionych z opodatkowania (załącznik ZIN-2, ZDN-2, ZIR-2, ZDR-2, ZIL-2 albo ZDL-2)",
      visibleWhen: CHOSEN,
      fields: [
        { code: "maZwolnione", type: "radio", label: "Czy posiadasz przedmioty opodatkowania zwolnione z opodatkowania?", required: true, options: YES_NO },
        {
          code: "zwolnione",
          type: "repeat",
          label: "Przedmioty zwolnione z opodatkowania",
          itemLabel: "Przedmiot zwolniony",
          required: true,
          visibleWhen: { field: "maZwolnione", equals: "tak" },
          min: 1,
          max: 100,
          fields: [
            ...subjectFields,
            {
              code: "podstawaZwolnienia",
              type: "text",
              label: "Podstawa prawna zwolnienia (jednostka redakcyjna ustawy lub uchwały rady gminy)",
              required: true,
              maxLength: 300,
            },
          ],
        },
      ],
    },
    {
      code: "pozostaliPodatnicy",
      title: "Dane pozostałych podatników (załącznik ZIN-3, ZIR-3 albo ZIL-3)",
      visibleWhen: { all: [INFO, { field: "rodzajPodmiotu", equals: "wspolwlasciciel" }] },
      fields: [
        {
          code: "wspolnieZPozostalymi",
          type: "radio",
          label: "Czy informację składasz wspólnie z pozostałymi współwłaścicielami lub współposiadaczami?",
          required: true,
          options: YES_NO,
          hint: "Każdy współwłaściciel może też złożyć własną informację.",
        },
        {
          code: "pozostaliPodatnicy",
          type: "repeat",
          label: "Pozostali podatnicy",
          itemLabel: "Podatnik",
          required: true,
          visibleWhen: JOINT,
          min: 1,
          max: 20,
          fields: [
            { code: "nazwisko", type: "text", label: "Nazwisko", required: true, width: "half", maxLength: 100 },
            { code: "imie", type: "text", label: "Pierwsze imię", required: true, width: "half", maxLength: 100 },
            { code: "identyfikator", type: "text", label: "Identyfikator podatkowy — PESEL albo NIP", required: true, width: "half", maxLength: 20 },
            { code: "regon", type: "text", label: "REGON (o ile został nadany)", width: "half", maxLength: 14 },
            { code: "adres", type: "text", label: "Adres zamieszkania (ulica, numer, kod pocztowy, miejscowość)", required: true, maxLength: 300 },
          ],
        },
      ],
    },
    {
      code: "dodatkowe",
      title: "Dodatkowe informacje",
      visibleWhen: CHOSEN,
      fields: [
        {
          code: "zdarzenie",
          type: "textarea",
          label:
            "Inne (np. określenie zdarzenia powodującego obowiązek złożenia informacji, deklaracji lub korekty, wskazanie gmin, na terenie których położone są użytki rolne stanowiące gospodarstwo rolne)",
          maxLength: 2000,
        },
        {
          code: "pouczenieKks",
          type: "info",
          label:
            "Pouczenie: za podanie nieprawdy lub zatajenie prawdy i przez to narażenie podatku na uszczuplenie grozi odpowiedzialność przewidziana w Kodeksie karnym skarbowym.",
        },
        {
          code: "pouczenieEgzekucja",
          type: "info",
          label:
            "W przypadku niewpłacenia w obowiązujących terminach zobowiązania podatkowego lub raty podatku albo wpłacenia ich w niepełnej wysokości deklaracja stanowi podstawę do wystawienia tytułu wykonawczego zgodnie z przepisami o postępowaniu egzekucyjnym w administracji.",
          visibleWhen: DECL,
        },
      ],
    },
    replySection("Sposób doręczania korespondencji w sprawie"),
  ],
  attachments: [
    {
      code: "pelnomocnictwoUpl",
      label: "Pełnomocnictwo do podpisywania informacji lub deklaracji (UPL-1P albo UPL-1)",
      hint: "Art. 80a Ordynacji podatkowej.",
      visibleWhen: { all: [REPRESENTING, { field: "umocowanie", equals: "pelnomocnictwo" }] },
      required: true,
    },
    {
      code: "podpisyPozostalych",
      label: "Informacja podpisana przez pozostałych podatników wskazanych w formularzu albo ich pełnomocnictwa",
      visibleWhen: JOINT,
      required: true,
      multiple: true,
    },
    {
      code: "dokumentyZrodlowe",
      label:
        "Dokumenty źródłowe zdarzenia (np. kopia aktu notarialnego, umowy, zawiadomienia o zmianie w ewidencji gruntów i budynków, decyzji o pozwoleniu na użytkowanie)",
      hint: "Praktyka urzędów — nieobowiązkowo.",
      multiple: true,
    },
    otherAttachments(),
  ],
};

export default A6;
